import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMe, useChangePassword, useLogoutAll } from '../api';
import { useAuth } from '../../store/auth';
import { PageLoader, ErrorState, StatusBadge } from '../../components/ui';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useToast } from '../../components/Toast';
import { ApiRequestError, apiBaseURL } from '../../lib/api';
import { dateTime } from '../../lib/format';

export function SettingsPage() {
  const me = useMe();
  const toast = useToast();
  const navigate = useNavigate();
  const logout = useAuth((s) => s.logout);
  const changePw = useChangePassword();
  const logoutAll = useLogoutAll();

  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [signOutAllOpen, setSignOutAllOpen] = useState(false);

  const submitPw = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next !== confirm) return toast('New passwords do not match', 'error');
    try {
      await changePw.mutateAsync({ currentPassword: cur, newPassword: next });
      toast('Password changed — please sign in again', 'success');
      await logout();
      navigate('/admin/login');
    } catch (err) {
      toast(err instanceof ApiRequestError ? err.message : 'Change failed', 'error');
    }
  };

  if (me.isLoading) return <PageLoader />;
  if (me.error) return <ErrorState message={(me.error as Error).message} onRetry={() => me.refetch()} />;
  const u = me.data!;

  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-ink-900">Settings</h1>
        <p className="text-sm text-ink-400">Your account and this console.</p>
      </div>

      {/* Profile */}
      <div className="card p-4">
        <h2 className="mb-3 text-sm font-semibold text-ink-700">My profile</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <Row label="Name" value={u.name} />
          <Row label="Email" value={u.email} />
          <Row label="Role" value={<span className="badge bg-blue-100 text-blue-700">{u.role}</span>} />
          <Row
            label="Status"
            value={<StatusBadge status={u.status === 'active' ? 'active' : 'disabled'} />}
          />
          <Row label="Last login" value={u.lastLoginAt ? dateTime(u.lastLoginAt) : 'never'} />
          <Row label="Member since" value={u.createdAt ? dateTime(u.createdAt) : '—'} />
          <Row
            label="Permissions"
            value={u.permissions.length ? u.permissions.join(', ') : 'All (role-based)'}
          />
        </div>
      </div>

      {/* Change password */}
      <form className="card p-4" onSubmit={submitPw}>
        <h2 className="mb-3 text-sm font-semibold text-ink-700">Change password</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block">
            <span className="label">Current</span>
            <input className="input" type="password" value={cur} onChange={(e) => setCur(e.target.value)} />
          </label>
          <label className="block">
            <span className="label">New</span>
            <input className="input" type="password" value={next} onChange={(e) => setNext(e.target.value)} />
          </label>
          <label className="block">
            <span className="label">Confirm new</span>
            <input className="input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </label>
        </div>
        <p className="mt-2 text-xs text-ink-400">
          At least 8 characters with an uppercase letter, a lowercase letter and a digit. You'll be
          signed out on all devices.
        </p>
        <button
          className="btn-primary mt-3"
          type="submit"
          disabled={changePw.isPending || !cur || next.length < 8}
        >
          Update password
        </button>
      </form>

      {/* Sessions */}
      <div className="card p-4">
        <h2 className="mb-1 text-sm font-semibold text-ink-700">Sessions</h2>
        <p className="text-sm text-ink-400">
          Sign out everywhere to revoke every refresh token issued to your account.
        </p>
        <button className="btn-outline mt-3" onClick={() => setSignOutAllOpen(true)}>
          Sign out all devices
        </button>
      </div>

      {/* About */}
      <div className="card p-4">
        <h2 className="mb-3 text-sm font-semibold text-ink-700">About</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <Row label="Console" value="Jewellery Exhibition — QR Booking & Analytics" />
          <Row label="API base" value={<span className="font-mono text-xs">{apiBaseURL}</span>} />
        </div>
      </div>

      <ConfirmDialog
        open={signOutAllOpen}
        title="Sign out all devices?"
        tone="primary"
        confirmLabel="Sign out everywhere"
        busy={logoutAll.isPending}
        message="Every device signed in as you will need to log in again, including this one."
        onCancel={() => setSignOutAllOpen(false)}
        onConfirm={async () => {
          try {
            await logoutAll.mutateAsync();
            await logout();
            navigate('/admin/login');
          } catch (e) {
            toast(e instanceof ApiRequestError ? e.message : 'Failed', 'error');
            setSignOutAllOpen(false);
          }
        }}
      />
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="label">{label}</p>
      <div className="text-sm text-ink-800">{value}</div>
    </div>
  );
}
